/**
 * grapesResizer.js — vanilla port of GrapesJS' Resizer.ts core math.
 *
 * Why this exists: GrapesJS itself was overkill for our use case (importing
 * complex AI-generated HTML), but its Resizer is the gold-standard algorithm
 * for pixel-precise resize with keep-ratio, edge constraints, and step
 * normalisation. We extracted ONLY the math + handle wiring so our own
 * editor can attach it to any DOM element.
 *
 * Usage:
 *   const stop = attachGrapesResizer(target, {
 *     onMove({ width, height, deltaW, deltaH }) { ... },
 *     onEnd({ width, height }) { ... },
 *     ratioDefault: false,           // hold Shift to lock ratio
 *     minDim: 16, maxDim: 4000,
 *     step: 1,
 *     scaleText: true,                // also scale font-size + padding for text tags
 *   });
 *   // Returns a cleanup fn that removes the handles.
 *
 * Pure DOM, no React, works inside or outside an iframe.
 *
 * Original algorithm: https://github.com/GrapesJS/grapesjs/blob/dev/packages/core/src/utils/Resizer.ts
 */

const HANDLES = ["tl", "tc", "tr", "cl", "cr", "bl", "bc", "br"];
// Pretty cursors per handle
const CURSORS = {
  tl: "nwse-resize", tr: "nesw-resize",
  bl: "nesw-resize", br: "nwse-resize",
  tc: "ns-resize",   bc: "ns-resize",
  cl: "ew-resize",   cr: "ew-resize",
};

const SCALE_TEXT_TAGS = /^(A|BUTTON|H[1-6]|P|SPAN|LI|LABEL)$/;

// CSS properties that should scale with the resize ratio. We snapshot their
// starting numeric values per descendant on pointerdown and reapply
// `start * ratio` on every move so the whole subtree grows/shrinks
// symmetrically — mimics Framer/Webflow's "scale group" behaviour.
const SCALE_PROPS = [
  "font-size",
  "padding-top", "padding-right", "padding-bottom", "padding-left",
  "margin-top", "margin-bottom",
  "gap", "row-gap", "column-gap",
  "border-radius",
  "letter-spacing",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
];

// Tags whose width/height we explicitly bump alongside the parent so icons
// and inline images don't stay tiny when their container grows.
const SCALE_DIM_TAGS = /^(IMG|SVG|VIDEO|IFRAME|CANVAS)$/;

/** Snapshot starting numeric values of SCALE_PROPS for one element. */
function snapshotScalable(el, win) {
  const cs = win.getComputedStyle(el);
  const snap = {};
  for (const prop of SCALE_PROPS) {
    const v = parseFloat(cs.getPropertyValue(prop));
    if (!isNaN(v) && v > 0) snap[prop] = v;
  }
  // Width/height for replaced elements (img, svg, etc.)
  if (SCALE_DIM_TAGS.test(el.tagName)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0)  snap["__w"] = r.width;
    if (r.height > 0) snap["__h"] = r.height;
  }
  return snap;
}

/** Walk subtree and snapshot scalable props for every descendant.
 *  Capped at 48 nodes to stay under the backend's 50-op-per-batch limit
 *  (we leave headroom for the parent op + safety buffer).
 */
function snapshotSubtree(root, win) {
  const out = [];
  const stack = [root];
  const MAX_NODES = 48;
  while (stack.length && out.length < MAX_NODES) {
    const el = stack.pop();
    // Skip our own resize handles + toolbar
    if (el.classList?.contains("__sy-grapes-handle")) continue;
    if (el.classList?.contains("__sy-resize-handle")) continue;
    if (el.id === "__sy_sel_toolbar" || el.id === "__sy_drop_indicator" || el.id === "__sy_drop_label") continue;
    if (el !== root) {
      const snap = snapshotScalable(el, win);
      if (Object.keys(snap).length) out.push({ el, snap });
    }
    if (el.children) {
      for (let i = 0; i < el.children.length; i++) stack.push(el.children[i]);
    }
  }
  return out;
}

/** Apply the current ratio to every snapshotted descendant. */
function applySubtreeScale(snapshots, ratio) {
  for (const { el, snap } of snapshots) {
    for (const prop of SCALE_PROPS) {
      const start = snap[prop];
      if (start) el.style.setProperty(prop, (start * ratio).toFixed(2) + "px");
    }
    if (snap.__w) el.style.width  = (snap.__w * ratio).toFixed(2) + "px";
    if (snap.__h) el.style.height = (snap.__h * ratio).toFixed(2) + "px";
  }
}

/** Build the style patch dict that must be persisted to the AST for one el. */
function buildSubtreePatches(snapshots, ratio) {
  // Returns a list of {nodeId, style} so the editor can dispatch
  // batch update_style ops after the user releases the mouse.
  const patches = [];
  for (const { el, snap } of snapshots) {
    const nodeId = el.getAttribute?.("data-sy-nodeid");
    if (!nodeId) continue;
    const stylePatch = {};
    for (const prop of SCALE_PROPS) {
      if (snap[prop]) stylePatch[prop] = (snap[prop] * ratio).toFixed(2) + "px";
    }
    if (snap.__w) stylePatch.width  = (snap.__w * ratio).toFixed(2) + "px";
    if (snap.__h) stylePatch.height = (snap.__h * ratio).toFixed(2) + "px";
    if (Object.keys(stylePatch).length) patches.push({ nodeId, style: stylePatch });
  }
  return patches;
}

/** Round value to a given step. Matches GrapesJS' normalizeFloat. */
function normalizeFloat(value, step) {
  return Math.round(value / step) * step;
}

/**
 * Core math from Resizer.calc — given the start dims + delta + handler
 * attribute (`tl`/`br`/etc.), returns the new {w, h, dl, dt} where
 * `dl`/`dt` are how much to shift the left/top of the element to keep
 * its visual anchor stable (only relevant for top/left handles).
 */
function calc({ delta, handlerAttr, startW, startH, keys, opts }) {
  const { step, minDim, maxDim, ratioDefault } = opts;

  let w = startW;
  let h = startH;
  let dl = 0;
  let dt = 0;

  if (handlerAttr.includes("r")) {
    const v = normalizeFloat(startW + delta.x * step, step);
    w = Math.max(minDim, Math.min(maxDim, v));
  } else if (handlerAttr.includes("l")) {
    const v = normalizeFloat(startW - delta.x * step, step);
    w = Math.max(minDim, Math.min(maxDim, v));
    dl = startW - w;
  }

  if (handlerAttr.includes("b")) {
    const v = normalizeFloat(startH + delta.y * step, step);
    h = Math.max(minDim, Math.min(maxDim, v));
  } else if (handlerAttr.includes("t")) {
    const v = normalizeFloat(startH - delta.y * step, step);
    h = Math.max(minDim, Math.min(maxDim, v));
    dt = startH - h;
  }

  // Aspect ratio enforcement
  // ratioDefault=false → Shift locks ratio
  // ratioDefault=true  → Shift unlocks ratio
  const ratioActive = ratioDefault ? !keys.shift : keys.shift;
  const isCenter = handlerAttr.includes("c") && handlerAttr.length === 2;
  if (ratioActive && !isCenter && startH > 0 && startW > 0) {
    const baseRatio = startW / startH;
    const proposedRatio = w / h;
    if (proposedRatio > baseRatio) {
      h = Math.round(w / baseRatio);
    } else {
      w = Math.round(h * baseRatio);
    }
    w = Math.max(minDim, Math.min(maxDim, w));
    h = Math.max(minDim, Math.min(maxDim, h));
  }

  return { w, h, dl, dt };
}

const HANDLE_STYLE = `
position: absolute;
width: 12px; height: 12px;
background: #FFC107;
border: 2px solid #fff;
border-radius: 50%;
box-shadow: 0 2px 8px rgba(0,0,0,0.4);
pointer-events: auto;
z-index: 999997;
`;
const EDGE_STYLE = `
position: absolute;
background: rgba(255,193,7,0.45);
pointer-events: auto;
z-index: 999996;
`;

/**
 * Attach 8 resize handles around `target` inside `target.ownerDocument.body`.
 *
 * Returns a `disposer` fn that removes the handles + cancels listeners.
 * Calling `disposer.update()` repositions the handles (e.g. after the
 * element scrolls or the iframe is resized).
 */
export function attachGrapesResizer(target, userOpts = {}) {
  if (!target) return () => {};
  const doc = target.ownerDocument;
  const win = doc.defaultView;
  const opts = {
    step: 1,
    minDim: 16,
    maxDim: 8000,
    ratioDefault: false,
    enableEdges: true,
    scaleText: true,
    onStart: () => {},
    onMove: () => {},
    onEnd: () => {},
    ...userOpts,
  };

  const handles = [];

  function makeHandle(pos) {
    const isCorner = pos.length === 2 && !pos.includes("c");
    const el = doc.createElement("div");
    el.className = `__sy-grapes-handle __sy-handle-${pos}`;
    el.setAttribute("data-pos", pos);
    el.style.cssText = isCorner ? HANDLE_STYLE : EDGE_STYLE;
    el.style.cursor = CURSORS[pos];
    if (!isCorner) {
      // Edge handles: thin bars
      if (pos === "tc" || pos === "bc") { el.style.height = "6px"; el.style.borderRadius = "3px"; }
      if (pos === "cl" || pos === "cr") { el.style.width = "6px"; el.style.borderRadius = "3px"; }
    }
    return el;
  }

  HANDLES.forEach(pos => {
    if (!opts.enableEdges && pos.length === 2 && pos.includes("c")) return;
    const h = makeHandle(pos);
    doc.body.appendChild(h);
    handles.push(h);
  });

  function position() {
    if (!doc.body.contains(target)) {
      // Element vanished (e.g. user deleted it). Hide handles.
      handles.forEach(h => h.style.display = "none");
      return;
    }
    const r = target.getBoundingClientRect();
    const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
    const sy = doc.documentElement.scrollTop  || doc.body.scrollTop  || 0;
    const left = r.left + sx, top = r.top + sy;
    const right = left + r.width, bottom = top + r.height;
    const cx = left + r.width / 2, cy = top + r.height / 2;
    const COORDS = {
      tl: [left,  top,    "corner"], tr: [right, top,    "corner"],
      bl: [left,  bottom, "corner"], br: [right, bottom, "corner"],
      tc: [left,  top,    "edge-h", r.width],
      bc: [left,  bottom, "edge-h", r.width],
      cl: [left,  top,    "edge-v", r.height],
      cr: [right, top,    "edge-v", r.height],
    };
    handles.forEach(h => {
      const pos = h.dataset.pos;
      const [x, y, kind, len] = COORDS[pos];
      h.style.display = "block";
      if (kind === "corner") {
        h.style.left = `${x - 6}px`;
        h.style.top  = `${y - 6}px`;
      } else if (kind === "edge-h") {
        h.style.left = `${x}px`;
        h.style.top  = `${y - 3}px`;
        h.style.width = `${len}px`;
      } else { /* edge-v */
        h.style.left = `${x - 3}px`;
        h.style.top  = `${y}px`;
        h.style.height = `${len}px`;
      }
    });
  }

  function attachDrag(handle) {
    let drag = null;
    handle.addEventListener("pointerdown", (e) => {
      e.preventDefault(); e.stopPropagation();
      const rect = target.getBoundingClientRect();
      const cs = win.getComputedStyle(target);
      const fontSize = parseFloat(cs.fontSize) || 16;
      const padX = parseFloat(cs.paddingLeft) || 0;
      const padY = parseFloat(cs.paddingTop) || 0;
      drag = {
        pos: handle.dataset.pos,
        startX: e.clientX, startY: e.clientY,
        startW: rect.width, startH: rect.height,
        startLeft: rect.left, startTop: rect.top,
        fontSize, padX, padY,
        scaleText: opts.scaleText && SCALE_TEXT_TAGS.test(target.tagName),
        wasInline: cs.display === "inline",
        // Snapshot every scalable prop on every descendant so we can
        // proportionally grow/shrink the whole subtree (Framer-style).
        subtreeSnap: opts.proportionalChildren ? snapshotSubtree(target, win) : [],
      };
      try { handle.setPointerCapture(e.pointerId); } catch {}
      doc.body.style.cursor = CURSORS[drag.pos];
      doc.body.style.userSelect = "none";
      if (drag.wasInline) target.style.display = "inline-block";
      opts.onStart({ target, ...drag });
    });
    handle.addEventListener("pointermove", (e) => {
      if (!drag || !e.buttons) return;
      const delta = { x: e.clientX - drag.startX, y: e.clientY - drag.startY };
      const keys = { shift: e.shiftKey, ctrl: e.ctrlKey, alt: e.altKey };
      const out = calc({
        delta,
        handlerAttr: drag.pos,
        startW: drag.startW,
        startH: drag.startH,
        keys,
        opts,
      });
      target.style.width = `${out.w}px`;
      // Replaced elements (img/video/iframe/canvas/svg) need explicit height.
      // Top/bottom handles → set height. Center-only handlers → leave height.
      const isReplaced = /^(IMG|VIDEO|IFRAME|CANVAS|SVG)$/.test(target.tagName);
      const isVerticalHandler = drag.pos.includes("t") || drag.pos.includes("b");
      if (isReplaced || isVerticalHandler) {
        target.style.height = `${out.h}px`;
      }
      // Compensate left/top so the visual anchor stays fixed during l/t drags
      if (out.dl) target.style.marginLeft = `${out.dl}px`;
      if (out.dt) target.style.marginTop  = `${out.dt}px`;

      // Proportional ratio used by both the parent's scaleText path and the
      // subtree scaler. Clamped so users can't crash text into 0 or balloon
      // it past 4x in a single drag.
      const ratio = drag.startW > 0
        ? Math.max(0.3, Math.min(4, out.w / drag.startW))
        : 1;

      // Scale text + padding for buttons/headings/paragraphs
      if (drag.scaleText) {
        target.style.fontSize = (drag.fontSize * ratio).toFixed(1) + "px";
        if (drag.padX) {
          target.style.paddingLeft  = (drag.padX * ratio).toFixed(1) + "px";
          target.style.paddingRight = (drag.padX * ratio).toFixed(1) + "px";
        }
        if (drag.padY) {
          target.style.paddingTop    = (drag.padY * ratio).toFixed(1) + "px";
          target.style.paddingBottom = (drag.padY * ratio).toFixed(1) + "px";
        }
      }
      // Scale the WHOLE subtree symmetrically (every descendant's
      // font-size/padding/gap/border-radius gets * ratio). This is what
      // makes resizing a card feel like Framer — buttons, icons and copy
      // grow together with the parent.
      if (drag.subtreeSnap.length) {
        applySubtreeScale(drag.subtreeSnap, ratio);
      }

      position();
      opts.onMove({ target, width: out.w, height: out.h, deltaW: out.w - drag.startW, deltaH: out.h - drag.startH, ratio });
    });
    handle.addEventListener("pointerup", (e) => {
      if (!drag) return;
      try { handle.releasePointerCapture(e.pointerId); } catch {}
      const stylePatch = { width: target.style.width };
      if (target.style.height) stylePatch.height = target.style.height;
      if (drag.wasInline) stylePatch.display = "inline-block";
      if (drag.scaleText) {
        if (target.style.fontSize)      stylePatch["font-size"]    = target.style.fontSize;
        if (target.style.paddingLeft)   stylePatch["padding-left"] = target.style.paddingLeft;
        if (target.style.paddingRight)  stylePatch["padding-right"] = target.style.paddingRight;
        if (target.style.paddingTop)    stylePatch["padding-top"]   = target.style.paddingTop;
        if (target.style.paddingBottom) stylePatch["padding-bottom"] = target.style.paddingBottom;
      }
      // Bundle every descendant's final style so the parent can persist
      // them via batched update_style ops on the AST.
      const finalRatio = drag.startW > 0
        ? Math.max(0.3, Math.min(4, parseFloat(target.style.width) / drag.startW))
        : 1;
      const subtreePatches = drag.subtreeSnap.length
        ? buildSubtreePatches(drag.subtreeSnap, finalRatio)
        : [];
      opts.onEnd({
        target,
        width: parseFloat(target.style.width),
        height: parseFloat(target.style.height || 0),
        stylePatch,
        subtreePatches,
        ratio: finalRatio,
      });
      drag = null;
      doc.body.style.cursor = "";
      doc.body.style.userSelect = "";
    });
  }

  handles.forEach(attachDrag);
  position();

  // Reposition on iframe scroll/resize
  const onScroll = () => position();
  win.addEventListener("scroll", onScroll, true);
  win.addEventListener("resize", onScroll);

  function dispose() {
    handles.forEach(h => h.remove());
    win.removeEventListener("scroll", onScroll, true);
    win.removeEventListener("resize", onScroll);
  }
  dispose.update = position;
  return dispose;
}
